/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { ComponentProps } from 'preact';
import { Menu } from '#UNSAFE_Menu';
import { initialMenuProps, MenuProps } from './util';
import { Ref, useCallback, Dispatch, StateUpdater } from 'preact/hooks';
import { ContextMenuConfig } from '#utils/UNSAFE_visTypes/common';
import type { gestureTypes } from './useContextMenu';

export type Props<T> = Omit<ComponentProps<typeof Menu>, 'children'> & {
  contextMenuConfig: ContextMenuConfig<T>;
} & { contextMenuContext: T } & { rootRef: Ref<HTMLElement> } & {
  setMenuProps: Dispatch<StateUpdater<MenuProps>>;
} & { onContextMenuDismissed?: (gesture: gestureTypes) => void;  
} & { gestureRef: Ref<gestureTypes>};

/**
 * The internal component used to render a custom context menu
 */
export function ContextMenu<T>({
  contextMenuConfig,
  contextMenuContext,
  rootRef,
  setMenuProps,
  onContextMenuDismissed,
  gestureRef,
  ...menuProps
}: Props<T>) {
  const handleCloseContextMenu = useCallback<Required<ComponentProps<typeof Menu>>['onClose']>(
    (detail) => {
      /* 
      TODO: Focus ring should be visible when context menu was closed because of selecting an item using keyboard, 
      but should not be visible when the selection was done using mouse or touch. This would probably means menu 
      onClose API is going to be changed a little bit so we can satisfy this requirement. JET-62372
      */
      //Focus has to be set on the current target that was obtained
      //during context menu gesture when menu is dismissed or item is "selected"
      if (detail.reason === 'dismissed' || detail.reason === 'itemAction') {
        rootRef.current?.focus({ preventScroll: true });
        onContextMenuDismissed?.(gestureRef.current);
      }
      // We close the menu.The only prop that matters here is isOpen
      setMenuProps(initialMenuProps);
    },
    [rootRef, setMenuProps, onContextMenuDismissed]
  );
  return (
    <Menu
      {...menuProps}
      onClose={handleCloseContextMenu}
      aria-label={contextMenuConfig.accessibleLabel}>
      {menuProps.isOpen && contextMenuConfig.itemsRenderer(contextMenuContext)}
    </Menu>
  );
}
